/* KUSTM Designer v2
 * Front/back design with image + text layers, drag / corner-resize / pinch / rotate,
 * print-area clamping, live DPI check, size-based print pricing, print-ready files,
 * mockup proofs, and a print-fee cart line linked to the garment by _design_id.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-kd]');
  if (!root) return;

  const $ = (s, el) => (el || root).querySelector(s);
  const $$ = (s, el) => Array.from((el || root).querySelectorAll(s));

  const product = JSON.parse($('[data-kd-product]').textContent);
  const cfg = JSON.parse($('[data-kd-config]').textContent);

  const canvas = $('[data-kd-canvas]');
  const mockupImg = $('[data-kd-mockup]');
  const zoneEl = $('[data-kd-zone]');
  const emptyEl = $('[data-kd-empty]');
  const fileInput = $('[data-kd-file]');
  const msgEl = $('[data-kd-msg]');
  const formMsgEl = $('[data-kd-form-msg]');
  const panel = $('[data-kd-layer-panel]');
  const textEditor = $('[data-kd-text-editor]');
  const textInput = $('[data-kd-text-input]');
  const sizeRange = $('[data-kd-size]');
  const rotRange = $('[data-kd-rotate]');
  const rotValue = $('[data-kd-rotate-value]');
  const qualityEl = $('[data-kd-quality]');
  const form = $('[data-kd-form]');
  const qtyInput = $('[data-kd-qty]');
  const addBtn = $('[data-kd-add]');
  const addLabel = $('[data-kd-add-label]');
  const clearBtn = $('[data-kd-clear]');
  const sticky = $('[data-kd-sticky]');

  const MAX_LAYERS = 4;
  const MAX_BYTES = 20 * 1024 * 1024;
  const OK_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
  const MIN_W_IN = 0.75;
  const PRINT_MAX_PX = 8e6;
  const CARRY_KEY = 'kustm-design-carry';

  const FONTS = [
    { id: 'anton', label: 'Anton', family: '"Anton"', weight: 400 },
    { id: 'space', label: 'Grotesk', family: '"Space Grotesk"', weight: 700 },
    { id: 'archivo', label: 'Archivo Black', family: '"Archivo Black"', weight: 400 },
    { id: 'marker', label: 'Marker', family: '"Permanent Marker"', weight: 400 },
    { id: 'playfair', label: 'Playfair', family: '"Playfair Display"', weight: 700 },
    { id: 'pacifico', label: 'Pacifico', family: '"Pacifico"', weight: 400 },
  ];
  const TEXT_COLORS = [
    { name: 'Black', hex: '#111111' }, { name: 'White', hex: '#FFFFFF' }, { name: 'Red', hex: '#D7141C' },
    { name: 'Cream', hex: '#EFE6D2' }, { name: 'Navy', hex: '#1F2A44' }, { name: 'Grey', hex: '#8A8A8A' },
    { name: 'Gold', hex: '#C9A227' }, { name: 'Green', hex: '#2D6A4F' },
  ];
  const GARMENT_COLORS = { white: '#FFFFFF', black: '#111111', heathergrey: '#B9B8B3', 'heather-grey': '#B9B8B3', grey: '#9A9A9A', gray: '#9A9A9A', cream: '#EFE6D2', navy: '#1F2A44', red: '#C1121F', green: '#2D6A4F', sand: '#D8C8A8', pink: '#F4B6C2', blue: '#3A6EA5' };

  // ---------- product key / zones ----------
  function detectKey() {
    const hay = [cfg.product_type, cfg.product_handle, cfg.product_title, (cfg.product_tags || []).join(' ')].join(' ').toLowerCase();
    const isYouth = /\byouth\b|\bkids?\b/.test(hay);
    const isInfant = /\binfant\b|\bbaby\b/.test(hay);
    const isOversized = /\bover-?\s?sized\b/.test(hay);
    let base = 'tshirt';
    if (hay.includes('long')) base = 'longsleeve';
    else if (hay.includes('hood')) base = 'hoodie';
    else if (hay.includes('jog')) base = 'joggers';
    if (base !== 'joggers') {
      if (isInfant) return 'infant_' + base;
      if (isYouth) return 'youth_' + base;
      if (isOversized && (base === 'tshirt' || base === 'hoodie')) return 'oversized_' + base;
    }
    return base;
  }
  const key = detectKey();
  const zone = (s) => (cfg.zones[s] && cfg.zones[s][key]) || cfg.zones[s].tshirt;

  // ---------- money ----------
  function formatMoney(cents) {
    const fmt = cfg.money_format || '${{amount}}';
    const n = (cents || 0) / 100;
    const withDec = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDec = Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, t) => {
      if (t === 'amount_no_decimals') return noDec;
      if (t === 'amount_with_comma_separator') return withDec.replace('.', ',');
      return withDec;
    }).replace(/<[^>]*>/g, '');
  }
  const dollars = (d) => formatMoney(Math.round(d * 100));

  // ---------- state ----------
  let side = 'front';
  let selected = null;
  let uid = 0;
  let submitting = false;
  let lowResAcknowledged = false;
  const layers = { front: [], back: [] };

  function allLayers() { return layers.front.concat(layers.back); }
  function findLayer(id) {
    for (const s of ['front', 'back']) {
      const l = layers[s].find((x) => x.id === id);
      if (l) return { l, s };
    }
    return null;
  }

  // Layer model (fractions of the side's print zone):
  // cx, cy = center; w = width as fraction of zone width; rot = degrees; aspect = art px width / px height
  function hOf(l, s) { const z = zone(s); return (l.w * z.width_in) / (l.aspect * z.height_in); }
  function inches(l, s) { const z = zone(s); return { w: l.w * z.width_in, h: hOf(l, s) * z.height_in }; }
  function rotBox(l, s) {
    const z = zone(s); const d = inches(l, s); const r = (l.rot || 0) * Math.PI / 180;
    const c = Math.abs(Math.cos(r)), sn = Math.abs(Math.sin(r));
    const bw = d.w * c + d.h * sn, bh = d.w * sn + d.h * c;
    return { bw: bw / z.width_in, bh: bh / z.height_in, bwIn: bw, bhIn: bh };
  }
  function maxScale(l, s) { const b = rotBox(l, s); return Math.min(1 / b.bw, 1 / b.bh); }
  function clampLayer(l, s) {
    const z = zone(s);
    const minW = MIN_W_IN / z.width_in;
    if (l.w < minW) l.w = minW;
    const m = maxScale(l, s);
    if (m < 1) l.w *= m;
    const b = rotBox(l, s);
    l.cx = Math.min(1 - b.bw / 2, Math.max(b.bw / 2, l.cx));
    l.cy = Math.min(1 - b.bh / 2, Math.max(b.bh / 2, l.cy));
  }
  function sideBounds(s) {
    if (!layers[s].length) return null;
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    layers[s].forEach((l) => {
      const b = rotBox(l, s);
      x0 = Math.min(x0, l.cx - b.bw / 2); x1 = Math.max(x1, l.cx + b.bw / 2);
      y0 = Math.min(y0, l.cy - b.bh / 2); y1 = Math.max(y1, l.cy + b.bh / 2);
    });
    const z = zone(s);
    return { x0, y0, x1, y1, wIn: (x1 - x0) * z.width_in, hIn: (y1 - y0) * z.height_in };
  }
  function sideFee(s) {
    const b = sideBounds(s);
    if (!b) return 0;
    const min = s === 'back' ? Number(cfg.min_fee_back) : Number(cfg.min_fee_front);
    const raw = b.wIn * b.hIn * Number(cfg.rate_per_sq_in);
    return Math.max(min, Math.round(raw)); // whole dollars: what we show is exactly what is charged
  }
  function feePerUnit() { return sideFee('front') + sideFee('back'); }
  function dpiOf(l, s) {
    if (l.type !== 'image') return null;
    if (l.vector) return Infinity;
    return l.naturalW / inches(l, s).w;
  }

  // ---------- messages ----------
  let msgTimer;
  function say(text, tone) {
    msgEl.textContent = text || '';
    msgEl.dataset.tone = tone || '';
    clearTimeout(msgTimer);
    if (text && tone !== 'error') msgTimer = setTimeout(() => { msgEl.textContent = ''; }, 5000);
  }
  function formSay(text) { formMsgEl.textContent = text || ''; }

  // ---------- variants ----------
  const optionNames = product.options.map((o) => (typeof o === 'string' ? o : o.name));
  function selectedOptions() {
    const out = {};
    $$('input[type="radio"]:checked', form).forEach((i) => {
      const m = i.name.match(/^options\[(.+)\]$/);
      if (m) out[m[1]] = i.value;
    });
    return out;
  }
  function currentVariant() {
    const o = selectedOptions();
    return product.variants.find((v) => optionNames.every((n, i) => v['option' + (i + 1)] === o[n])) || null;
  }
  function slug(v) { return String(v || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function currentColorKey() {
    const o = selectedOptions();
    const name = optionNames.find((n) => /^colou?r$/i.test(n));
    return slug(name ? o[name] : 'white') || 'white';
  }
  function mockupFor(s, colorKey) {
    const m = (cfg.mockups && cfg.mockups[key]) || {};
    const c = m[colorKey] || {};
    if (c[s]) return c[s];
    if (s === 'front') return cfg.featured_image || mockupImg.src;
    const first = Object.values(m).find((x) => x && x.back);
    return first ? first.back : (cfg.featured_image || mockupImg.src);
  }
  function setMockup() {
    const target = mockupFor(side, currentColorKey());
    if (mockupImg.getAttribute('src') !== target) {
      canvas.classList.add('is-swapping');
      const pre = new Image();
      pre.onload = pre.onerror = () => { mockupImg.src = target; canvas.classList.remove('is-swapping'); };
      pre.src = target;
    }
    mockupImg.alt = `${cfg.product_title}, ${side} view`;
  }
  function onVariantChange() {
    const v = currentVariant();
    const o = selectedOptions();
    $$('[data-kd-option-value]').forEach((el) => { el.textContent = o[el.dataset.kdOptionValue] || ''; });
    if (v) {
      $('[data-kd-base-price]').textContent = formatMoney(v.price);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url.toString());
    }
    setMockup();
    updateSummary();
  }
  $$('input[type="radio"]', form).forEach((i) => i.addEventListener('change', onVariantChange));
  $$('.kd-swatch__dot').forEach((d) => {
    const c = GARMENT_COLORS[d.dataset.color] || GARMENT_COLORS[slug(d.dataset.color)];
    if (c) d.style.background = c;
  });

  // ---------- qty ----------
  function qty() { return Math.max(1, Math.min(500, parseInt(qtyInput.value, 10) || 1)); }
  $$('[data-kd-qty-step]').forEach((b) => b.addEventListener('click', () => {
    qtyInput.value = Math.max(1, Math.min(500, qty() + Number(b.dataset.kdQtyStep)));
    updateSummary();
  }));
  qtyInput.addEventListener('input', updateSummary);
  qtyInput.addEventListener('blur', () => { qtyInput.value = qty(); updateSummary(); });

  // ---------- rendering ----------
  function layerLabel(l) { return l.type === 'text' ? `Text: ${l.text.split('\n')[0]}` : `Image: ${l.name || 'artwork'}`; }

  function createLayerEl(l) {
    const el = document.createElement('div');
    el.className = 'kd-layer';
    el.dataset.id = l.id;
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    const img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    el.appendChild(img);
    const h = document.createElement('span');
    h.className = 'kd-handle';
    h.setAttribute('aria-hidden', 'true');
    el.appendChild(h);
    el.addEventListener('keydown', onLayerKey);
    el.addEventListener('focus', () => { if (selected !== l.id) select(l.id); });
    return el;
  }

  function render() {
    const z = zone(side);
    Object.assign(zoneEl.style, { top: z.top + '%', left: z.left + '%', width: z.width + '%', height: z.height + '%' });
    $('[data-kd-zone-size]').textContent = `${z.width_in} × ${z.height_in} in`;

    $$('.kd-layer', canvas).forEach((el) => { if (!layers[side].some((l) => l.id === el.dataset.id)) el.remove(); });
    layers[side].forEach((l, i) => {
      let el = canvas.querySelector(`.kd-layer[data-id="${l.id}"]`);
      if (!el) { el = createLayerEl(l); canvas.appendChild(el); }
      const h = hOf(l, side);
      el.style.left = (z.left + (l.cx - l.w / 2) * z.width) + '%';
      el.style.top = (z.top + (l.cy - h / 2) * z.height) + '%';
      el.style.width = (l.w * z.width) + '%';
      el.style.height = (h * z.height) + '%';
      el.style.transform = l.rot ? `rotate(${l.rot}deg)` : '';
      el.style.zIndex = String(10 + i);
      const img = el.querySelector('img');
      if (img.getAttribute('src') !== l.src) img.src = l.src;
      el.classList.toggle('is-selected', l.id === selected);
      el.setAttribute('aria-label', `${layerLabel(l)}. Arrow keys move, plus and minus resize, Delete removes.`);
    });
    zoneEl.classList.toggle('has-art', layers[side].length > 0);
    emptyEl.hidden = layers[side].length > 0;
    clearBtn.hidden = layers[side].length === 0;
    $$('[data-kd-side]').forEach((t) => { t.dataset.hasArt = layers[t.dataset.kdSide].length ? '1' : ''; });
    renderPanel();
    updateSummary();
  }

  function renderPanel() {
    const f = selected && findLayer(selected);
    if (!f || f.s !== side) { panel.hidden = true; return; }
    const { l } = f;
    panel.hidden = false;
    textEditor.hidden = l.type !== 'text';
    if (l.type === 'text') {
      if (document.activeElement !== textInput) textInput.value = l.text;
      $$('[data-kd-font]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.kdFont === l.font)));
      $$('[data-kd-color]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.kdColor === l.color)));
    }
    const m = maxScale(l, side) * l.w;
    sizeRange.value = String(Math.round(Math.min(100, (l.w / m) * 100)));
    rotRange.value = String(Math.round(l.rot || 0));
    rotValue.textContent = `${Math.round(l.rot || 0)}°`;
    const dpi = dpiOf(l, side);
    if (dpi === null) { qualityEl.hidden = true; }
    else {
      qualityEl.hidden = false;
      let tone = 'good', label = 'Great print quality';
      if (dpi === Infinity) label = 'Vector art: sharp at any size';
      else if (dpi < 100) { tone = 'bad'; label = 'Low resolution: may print blurry. Make it smaller or upload a larger file'; }
      else if (dpi < 150) { tone = 'ok'; label = 'Good, may look slightly soft up close'; }
      qualityEl.dataset.tone = tone;
      qualityEl.textContent = dpi === Infinity ? label : `${label} (${Math.round(dpi)} DPI)`;
    }
  }

  function updateSummary() {
    const v = currentVariant();
    const base = v ? v.price : (product.price || 0);
    const fF = sideFee('front'), fB = sideFee('back');
    const bF = sideBounds('front'), bB = sideBounds('back');
    $('[data-kd-sum-base]').textContent = formatMoney(base);
    $('[data-kd-sum-front]').textContent = bF ? dollars(fF) : '—';
    $('[data-kd-sum-back]').textContent = bB ? dollars(fB) : '—';
    $('[data-kd-sum-front-size]').textContent = bF ? `${bF.wIn.toFixed(1)} × ${bF.hIn.toFixed(1)} in` : '';
    $('[data-kd-sum-back-size]').textContent = bB ? `${bB.wIn.toFixed(1)} × ${bB.hIn.toFixed(1)} in` : '';
    const each = base + (fF + fB) * 100;
    const total = each * qty();
    $('[data-kd-sum-each]').textContent = formatMoney(each);
    $('[data-kd-sum-qty]').textContent = qty() > 1 ? `× ${qty()}` : '';
    $('[data-kd-total]').textContent = formatMoney(total);
    $('[data-kd-sticky-total]').textContent = formatMoney(total);
    if (!submitting) {
      const available = v && v.available;
      addBtn.disabled = !available;
      addLabel.textContent = !v ? 'Unavailable' : !available ? 'Sold out' : `Add to cart · ${formatMoney(total)}`;
    }
  }

  // ---------- selection ----------
  function select(id) {
    selected = id;
    render();
  }

  // ---------- side tabs ----------
  function showSide(s) {
    if (s === side) return;
    side = s;
    selected = null;
    $$('[data-kd-side]').forEach((t) => {
      const on = t.dataset.kdSide === s;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
    setMockup();
    render();
  }
  $$('[data-kd-side]').forEach((t) => t.addEventListener('click', () => showSide(t.dataset.kdSide)));

  // ---------- adding layers ----------
  function addLayer(l, s) {
    s = s || side;
    if (layers[s].length >= MAX_LAYERS) { say(`You can add up to ${MAX_LAYERS} items per side.`, 'error'); return false; }
    l.id = 'L' + (++uid);
    layers[s].push(l);
    clampLayer(l, s);
    if (s === side) select(l.id);
    return true;
  }

  function loadImage(src) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error('Could not read that image.'));
      img.src = src;
    });
  }

  async function addImageFile(file, opts) {
    opts = opts || {};
    if (!file) return;
    const type = file.type || '';
    if (!OK_TYPES.includes(type)) { say('Please upload a PNG, JPG, WebP or SVG file.', 'error'); return; }
    if (file.size > MAX_BYTES) { say('That file is over 20 MB. Please upload a smaller version.', 'error'); return; }
    const src = URL.createObjectURL(file);
    let img;
    try { img = await loadImage(src); } catch (e) { say(e.message, 'error'); return; }
    const vector = type === 'image/svg+xml';
    const nw = img.naturalWidth || 1000, nh = img.naturalHeight || 1000;
    const l = {
      type: 'image', src, file, name: file.name, imgEl: img, vector,
      naturalW: nw, naturalH: nh, aspect: nw / nh, rot: 0, cx: 0.5, cy: 0.5, w: 1,
    };
    if (opts.restore) Object.assign(l, opts.restore, { src, file, imgEl: img });
    else {
      const m = maxScale(l, side);
      l.w = Math.min(1, m) * 0.7;
      // pull it toward the top of the zone, like a chest / upper-back print
      l.cy = Math.min(0.5, hOf(l, side) / 2 + 0.08);
    }
    if (addLayer(l, opts.side)) {
      if (!opts.restore) {
        const dpi = dpiOf(l, side);
        say(dpi !== Infinity && dpi < 100 ? 'Added. Heads up: this image is low resolution at this size.' : 'Added. Drag to place it.', dpi < 100 ? 'warn' : '');
      }
    }
  }

  $('[data-kd-upload]').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', (e) => { addImageFile(e.target.files[0]); fileInput.value = ''; });
  ['dragenter', 'dragover'].forEach((t) => canvas.addEventListener(t, (e) => { e.preventDefault(); canvas.classList.add('is-drop'); }));
  ['dragleave', 'drop'].forEach((t) => canvas.addEventListener(t, (e) => { e.preventDefault(); canvas.classList.remove('is-drop'); }));
  canvas.addEventListener('drop', (e) => { const f = e.dataTransfer && e.dataTransfer.files[0]; if (f) addImageFile(f); });

  // ---------- text ----------
  const fontById = (id) => FONTS.find((f) => f.id === id) || FONTS[0];
  async function ensureFont(f) {
    try { await document.fonts.load(`${f.weight} 100px ${f.family}`, 'AaBb'); } catch (_) {}
  }
  async function drawText(l, targetH) {
    const f = fontById(l.font);
    await ensureFont(f);
    const size = 200;
    const lines = (l.text || ' ').split('\n');
    const lh = size * 1.12;
    const pad = size * 0.18;
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    ctx.font = `${f.weight} ${size}px ${f.family}, sans-serif`;
    const maxW = Math.max(1, ...lines.map((t) => ctx.measureText(t).width));
    const W = Math.ceil(maxW + pad * 2);
    const H = Math.ceil(lines.length * lh + pad * 2);
    const scale = targetH ? targetH / H : 1;
    c.width = Math.max(1, Math.round(W * scale));
    c.height = Math.max(1, Math.round(H * scale));
    ctx.scale(scale, scale);
    ctx.font = `${f.weight} ${size}px ${f.family}, sans-serif`;
    ctx.fillStyle = l.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((t, i) => ctx.fillText(t, W / 2, pad + lh * i + lh / 2));
    return { canvas: c, aspect: W / H };
  }
  async function refreshText(l, s) {
    const physH = l.w ? hOf(l, s) * zone(s).height_in : null;
    const out = await drawText(l, 360);
    l.src = out.canvas.toDataURL('image/png');
    l.aspect = out.aspect;
    if (physH) l.w = (physH * l.aspect) / zone(s).width_in; // keep the letter height, let width follow the text
    clampLayer(l, s);
  }
  async function addText(opts) {
    opts = opts || {};
    const s = opts.side || side;
    if (layers[s].length >= MAX_LAYERS) { say(`You can add up to ${MAX_LAYERS} items per side.`, 'error'); return; }
    const l = Object.assign({ type: 'text', text: 'YOUR TEXT', font: 'anton', color: defaultTextColor(), rot: 0, cx: 0.5, cy: 0.3, w: 0 }, opts.restore || {});
    await refreshText(l, s);
    if (!opts.restore) {
      l.w = Math.min(l.w * maxScale(l, s), 0.7);
      const existing = sideBounds(s);
      l.cy = existing && existing.y1 < 0.75 ? Math.min(0.9, existing.y1 + hOf(l, s) / 2 + 0.04) : 0.25;
    }
    if (addLayer(l, s) && !opts.restore) {
      say('Text added. Type your words in the text box.');
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) setTimeout(() => { textInput.focus({ preventScroll: true }); textInput.select(); }, 50);
    }
  }
  function defaultTextColor() {
    const c = currentColorKey();
    return ['black', 'navy', 'heathergrey', 'green', 'red'].includes(c) ? '#FFFFFF' : '#111111';
  }
  $('[data-kd-add-text]').addEventListener('click', () => addText());

  const fontsWrap = $('[data-kd-fonts]');
  FONTS.forEach((f) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'kd-font';
    b.dataset.kdFont = f.id;
    b.setAttribute('role', 'radio');
    b.style.fontFamily = f.family;
    b.style.fontWeight = f.weight;
    b.textContent = f.label;
    fontsWrap.appendChild(b);
  });
  const colorsWrap = $('[data-kd-colors]');
  TEXT_COLORS.forEach((c) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'kd-color';
    b.dataset.kdColor = c.hex;
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-label', c.name);
    b.title = c.name;
    b.style.background = c.hex;
    colorsWrap.appendChild(b);
  });
  async function editSelectedText(patch) {
    const f = selected && findLayer(selected);
    if (!f || f.l.type !== 'text') return;
    Object.assign(f.l, patch);
    await refreshText(f.l, f.s);
    render();
  }
  let textTimer;
  textInput.addEventListener('input', () => {
    clearTimeout(textTimer);
    const val = textInput.value.replace(/\n{3,}/g, '\n\n');
    textTimer = setTimeout(() => editSelectedText({ text: val.trim() ? val : ' ' }), 120);
  });
  fontsWrap.addEventListener('click', (e) => { const b = e.target.closest('[data-kd-font]'); if (b) editSelectedText({ font: b.dataset.kdFont }); });
  colorsWrap.addEventListener('click', (e) => { const b = e.target.closest('[data-kd-color]'); if (b) editSelectedText({ color: b.dataset.kdColor }); });

  // ---------- layer controls ----------
  function withSelected(fn) {
    const f = selected && findLayer(selected);
    if (!f) return;
    fn(f.l, f.s);
    clampLayer(f.l, f.s);
    render();
  }
  sizeRange.addEventListener('input', () => withSelected((l, s) => {
    const cur = maxScale(l, s) * l.w; // width at 100%
    l.w = Math.max(MIN_W_IN / zone(s).width_in, cur * Number(sizeRange.value) / 100);
  }));
  rotRange.addEventListener('input', () => withSelected((l) => {
    let r = Number(rotRange.value);
    if (Math.abs(r) < 3) r = 0;
    l.rot = r;
  }));
  $('[data-kd-center]').addEventListener('click', () => withSelected((l) => { l.cx = 0.5; }));
  $('[data-kd-straighten]').addEventListener('click', () => withSelected((l) => { l.rot = 0; }));
  $('[data-kd-forward]').addEventListener('click', () => withSelected((l, s) => {
    const arr = layers[s]; const i = arr.indexOf(l);
    if (i < arr.length - 1) { arr.splice(i, 1); arr.push(l); }
  }));
  function removeLayer(id) {
    const f = findLayer(id);
    if (!f) return;
    layers[f.s] = layers[f.s].filter((x) => x !== f.l);
    if (f.l.type === 'image' && f.l.src.startsWith('blob:')) URL.revokeObjectURL(f.l.src);
    selected = null;
    render();
    say('Removed.');
  }
  $('[data-kd-delete]').addEventListener('click', () => selected && removeLayer(selected));
  clearBtn.addEventListener('click', () => {
    layers[side].forEach((l) => { if (l.type === 'image' && l.src.startsWith('blob:')) URL.revokeObjectURL(l.src); });
    layers[side] = [];
    selected = null;
    render();
    say(`Cleared the ${side}.`);
  });

  function onLayerKey(e) {
    const id = e.currentTarget.dataset.id;
    const f = findLayer(id);
    if (!f) return;
    const step = e.shiftKey ? 0.05 : 0.01;
    const map = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (map[e.key]) {
      e.preventDefault();
      f.l.cx += map[e.key][0]; f.l.cy += map[e.key][1];
    } else if (e.key === '+' || e.key === '=') { e.preventDefault(); f.l.w *= 1.05; }
    else if (e.key === '-' || e.key === '_') { e.preventDefault(); f.l.w *= 0.95; }
    else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeLayer(id); return; }
    else if (e.key === 'Escape') { select(null); return; }
    else return;
    clampLayer(f.l, f.s);
    render();
    const el = canvas.querySelector(`.kd-layer[data-id="${id}"]`);
    if (el) el.focus({ preventScroll: true });
  }

  // ---------- pointer gestures ----------
  const pointers = new Map();
  let gesture = null;
  function startGesture(mode) {
    const f = selected && findLayer(selected);
    if (!f) { gesture = null; return; }
    const pts = Array.from(pointers.values()).map((p) => ({ x: p.x, y: p.y }));
    const g = { l: f.l, s: f.s, mode, rect: canvas.getBoundingClientRect(), cx: f.l.cx, cy: f.l.cy, w: f.l.w, rot: f.l.rot || 0, pts };
    if (pts.length >= 2) {
      g.mode = 'pinch';
      g.d0 = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y) || 1;
      g.a0 = Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x);
    }
    if (g.mode === 'resize') {
      const z = zone(f.s);
      g.centerPx = {
        x: g.rect.left + ((z.left + f.l.cx * z.width) / 100) * g.rect.width,
        y: g.rect.top + ((z.top + f.l.cy * z.height) / 100) * g.rect.height,
      };
      g.r0 = Math.hypot(pts[0].x - g.centerPx.x, pts[0].y - g.centerPx.y) || 1;
    }
    gesture = g;
  }
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const el = e.target.closest('.kd-layer');
    const onHandle = !!e.target.closest('.kd-handle');
    if (!el && pointers.size === 0) { if (selected) select(null); return; }
    if (el && el.dataset.id !== selected && pointers.size === 0) select(el.dataset.id);
    e.preventDefault();
    try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    startGesture(onHandle ? 'resize' : 'drag');
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!pointers.has(e.pointerId) || !gesture) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture, l = g.l, z = zone(g.s);
    const pts = Array.from(pointers.values());
    const zw = (g.rect.width * z.width) / 100, zh = (g.rect.height * z.height) / 100;
    if (g.mode === 'drag') {
      l.cx = g.cx + (pts[0].x - g.pts[0].x) / zw;
      l.cy = g.cy + (pts[0].y - g.pts[0].y) / zh;
    } else if (g.mode === 'resize') {
      const r = Math.hypot(pts[0].x - g.centerPx.x, pts[0].y - g.centerPx.y);
      l.w = g.w * (r / g.r0);
    } else if (g.mode === 'pinch' && pts.length >= 2) {
      const d = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      const a = Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x);
      l.w = g.w * (d / g.d0);
      let rot = g.rot + ((a - g.a0) * 180) / Math.PI;
      rot = ((rot + 540) % 360) - 180;
      if (Math.abs(rot) < 4) rot = 0;
      l.rot = rot;
    }
    clampLayer(l, g.s);
    render();
  });
  function endPointer(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
    if (pointers.size) startGesture('drag'); else gesture = null;
  }
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  // ---------- print files & proofs ----------
  async function drawLayers(ctx, s, box) {
    // box: {x, y, w, h} px rect of the print zone on the target canvas
    for (const l of layers[s]) {
      const lw = l.w * box.w;
      const lh = hOf(l, s) * box.h;
      const cx = box.x + l.cx * box.w;
      const cy = box.y + l.cy * box.h;
      ctx.save();
      ctx.translate(cx, cy);
      if (l.rot) ctx.rotate((l.rot * Math.PI) / 180);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      if (l.type === 'image') ctx.drawImage(l.imgEl, -lw / 2, -lh / 2, lw, lh);
      else {
        const t = await drawText(l, Math.max(8, Math.round(lh)));
        ctx.drawImage(t.canvas, -lw / 2, -lh / 2, lw, lh);
      }
      ctx.restore();
    }
  }
  function toBlob(c, type, q) {
    return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('Could not create the print file.'))), type, q));
  }
  async function buildPrintFile(s, designId) {
    const z = zone(s);
    const dpi = Math.min(300, Math.floor(Math.sqrt(PRINT_MAX_PX / (z.width_in * z.height_in))));
    const c = document.createElement('canvas');
    c.width = Math.round(z.width_in * dpi);
    c.height = Math.round(z.height_in * dpi);
    const ctx = c.getContext('2d');
    await drawLayers(ctx, s, { x: 0, y: 0, w: c.width, h: c.height });
    const blob = await toBlob(c, 'image/png');
    return { file: new File([blob], `${designId}-${s}-print-${z.width_in}x${z.height_in}in-${dpi}dpi.png`, { type: 'image/png' }), dpi };
  }
  async function buildProof(s, designId) {
    const size = 1000;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#EDEDED';
    ctx.fillRect(0, 0, size, size);
    try {
      const url = mockupFor(s, currentColorKey());
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url + (url.includes('?') ? '&' : '?') + 'width=1000'; });
      ctx.drawImage(img, 0, 0, size, size);
    } catch (_) { /* proof still useful without the garment photo */ }
    const z = zone(s);
    await drawLayers(ctx, s, { x: (z.left / 100) * size, y: (z.top / 100) * size, w: (z.width / 100) * size, h: (z.height / 100) * size });
    try {
      const blob = await toBlob(c, 'image/jpeg', 0.85);
      return new File([blob], `${designId}-${s}-proof.jpg`, { type: 'image/jpeg' });
    } catch (_) { return null; }
  }
  function textSummary(l) {
    const f = fontById(l.font);
    const c = TEXT_COLORS.find((x) => x.hex === l.color);
    return `"${l.text.replace(/\n/g, ' / ')}" (${f.label}, ${c ? c.name : l.color})`;
  }
  function layoutJson(s) {
    const z = zone(s);
    return JSON.stringify({
      zone_in: [z.width_in, z.height_in],
      layers: layers[s].map((l) => {
        const d = inches(l, s);
        return {
          type: l.type, cx: +l.cx.toFixed(4), cy: +l.cy.toFixed(4), w_in: +d.w.toFixed(2), h_in: +d.h.toFixed(2), rot: Math.round(l.rot || 0),
          ...(l.type === 'text' ? { text: l.text, font: fontById(l.font).label, color: l.color } : { file: l.name, dpi: l.vector ? 'vector' : Math.round(dpiOf(l, s)) }),
        };
      }),
    });
  }

  // ---------- add to cart ----------
  function setBusy(on, label) {
    submitting = on;
    addBtn.disabled = on;
    addBtn.classList.toggle('is-busy', on);
    if (label) addLabel.textContent = label;
    if (!on) updateSummary();
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (submitting) return;
    formSay('');
    const v = currentVariant();
    if (!v) { formSay('Please choose your options.'); return; }
    if (!v.available) { formSay('That option is sold out. Please pick another.'); return; }
    if (!allLayers().length) {
      formSay('Add your art or text to the front or back first.');
      $('[data-kd-upload]').focus();
      return;
    }
    const lowRes = ['front', 'back'].some((s) => layers[s].some((l) => { const d = dpiOf(l, s); return d !== null && d < 100; }));
    if (lowRes && !lowResAcknowledged) {
      lowResAcknowledged = true;
      formSay('One of your images is low resolution and may print blurry. Make it smaller, upload a bigger file, or tap Add to cart again to order anyway.');
      return;
    }
    if (!cfg.fee_variant_id) { formSay('Printing can’t be priced right now. Please contact us to order.'); return; }

    const q = qty();
    const fee = feePerUnit();
    const designId = 'K' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();
    setBusy(true, 'Preparing your print files…');

    try {
      const hidden = document.createElement('form');
      hidden.method = 'post';
      hidden.action = cfg.cart_add_url;
      hidden.enctype = 'multipart/form-data';
      hidden.hidden = true;
      const field = (name, value) => { const i = document.createElement('input'); i.type = 'hidden'; i.name = name; i.value = value; hidden.appendChild(i); };
      const fileField = (name, file) => {
        if (!file) return;
        const i = document.createElement('input');
        i.type = 'file';
        i.name = name;
        const dt = new DataTransfer();
        dt.items.add(file);
        i.files = dt.files;
        hidden.appendChild(i);
      };

      field('id', String(v.id));
      field('quantity', String(q));
      for (const s of ['front', 'back']) {
        if (!layers[s].length) continue;
        const S = s === 'front' ? 'Front' : 'Back';
        const b = sideBounds(s);
        const kinds = layers[s].map((l) => l.type).filter((t, i, a) => a.indexOf(t) === i).join(' + ');
        field(`properties[${S} design]`, `${b.wIn.toFixed(1)}″ × ${b.hIn.toFixed(1)}″ ${kinds}`);
        const texts = layers[s].filter((l) => l.type === 'text');
        if (texts.length) field(`properties[${S} text]`, texts.map(textSummary).join(' | '));
        const proof = await buildProof(s, designId);
        fileField(`properties[${S} proof]`, proof);
        const print = await buildPrintFile(s, designId);
        fileField(`properties[${S} print file]`, print.file);
        layers[s].filter((l) => l.type === 'image').forEach((l, i) => fileField(`properties[${S} original art ${i + 1}]`, l.file));
        field(`properties[_${s}_layout]`, layoutJson(s));
      }
      field('properties[Printing]', `${dollars(fee)} per item`);
      field('properties[_design_id]', designId);
      field('properties[_fee_per_unit]', String(fee));

      setBusy(true, 'Adding to cart…');
      if (fee > 0) {
        const unitCents = Number(cfg.fee_unit_cents) || 100;
        const feeQty = Math.round((fee * 100) / unitCents) * q;
        const res = await fetch(cfg.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(cfg.fee_variant_id), quantity: feeQty, properties: { _design_id: designId, For: `${cfg.product_title} · ${v.title}` } }] }),
        });
        if (!res.ok) {
          let detail = '';
          try { detail = (await res.json()).description || ''; } catch (_) {}
          throw new Error(detail || 'We couldn’t add printing to your cart.');
        }
      }
      try { sessionStorage.removeItem(CARRY_KEY); } catch (_) {}
      window.removeEventListener('beforeunload', guardUnload);
      document.body.appendChild(hidden);
      hidden.submit();
    } catch (err) {
      console.error('[KUSTM] add to cart failed', err);
      formSay(`${err.message || err} Please try again.`);
      setBusy(false);
    }
  });

  // ---------- sticky mobile bar ----------
  $('[data-kd-sticky-add]').addEventListener('click', () => {
    if (!allLayers().length) { formSay('Add your art or text to the front or back first.'); canvas.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    form.requestSubmit ? form.requestSubmit() : addBtn.click();
  });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      const visible = entries[0].isIntersecting;
      sticky.classList.toggle('is-visible', !visible);
      sticky.setAttribute('aria-hidden', String(visible));
    }).observe(addBtn);
  }

  // ---------- don't lose work ----------
  function guardUnload(e) {
    if (!submitting && allLayers().length) { e.preventDefault(); e.returnValue = ''; }
  }
  window.addEventListener('beforeunload', guardUnload);

  // carry the design to another product
  function fileToDataUrl(file) {
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  }
  async function saveCarry() {
    const data = { front: [], back: [] };
    let budget = 4.2 * 1024 * 1024;
    for (const s of ['front', 'back']) {
      for (const l of layers[s]) {
        const base = { type: l.type, cx: l.cx, cy: l.cy, w: l.w, rot: l.rot || 0 };
        if (l.type === 'text') data[s].push(Object.assign(base, { text: l.text, font: l.font, color: l.color }));
        else if (l.file && l.file.size * 1.37 < budget) {
          budget -= l.file.size * 1.37;
          data[s].push(Object.assign(base, { dataUrl: await fileToDataUrl(l.file), name: l.name, mime: l.file.type }));
        }
      }
    }
    try { sessionStorage.setItem(CARRY_KEY, JSON.stringify(data)); return true; } catch (_) { return false; }
  }
  async function restoreCarry() {
    let data;
    try { data = JSON.parse(sessionStorage.getItem(CARRY_KEY) || 'null'); sessionStorage.removeItem(CARRY_KEY); } catch (_) { data = null; }
    if (!data) return;
    let n = 0;
    for (const s of ['front', 'back']) {
      for (const d of data[s] || []) {
        const restore = { cx: d.cx, cy: d.cy, w: d.w, rot: d.rot };
        if (d.type === 'text') { await addText({ side: s, restore: Object.assign(restore, { text: d.text, font: d.font, color: d.color }) }); n++; }
        else if (d.dataUrl) {
          const blob = await (await fetch(d.dataUrl)).blob();
          await addImageFile(new File([blob], d.name || 'artwork', { type: d.mime || blob.type }), { side: s, restore });
          n++;
        }
      }
    }
    selected = null;
    render();
    if (n) say('Your design came with you. Check the placement on this piece.');
  }
  $$('[data-kd-switch]').forEach((a) => a.addEventListener('click', async (e) => {
    if (a.getAttribute('aria-current') === 'page' || !allLayers().length) return;
    e.preventDefault();
    await saveCarry();
    window.removeEventListener('beforeunload', guardUnload);
    window.location.href = a.href;
  }));

  // ---------- init ----------
  const curSwitch = $('.kd-switcher__item.is-current');
  if (curSwitch) { const list = curSwitch.closest('.kd-switcher__list'); list.scrollLeft = Math.max(0, curSwitch.parentElement.offsetLeft - list.offsetLeft - 8); }
  onVariantChange();
  render();
  restoreCarry();
  root.classList.add('is-ready');
})();
